import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import { localizedError } from "../lib/localized-error";

type AdminTab = "overview" | "plans" | "packs" | "operations";

interface Overview {
  registered_dau: number;
  active_guest_sessions: number;
  new_users: number;
  total_users: number;
  confirmed_audio_seconds: number;
  transcription_users: { registered?: number; guests?: number };
  jobs: { created?: number; completed?: number; failed?: number; rejected?: number; cancelled?: number; completion_rate?: number | null };
  subscriptions: { active?: number; monthly?: number; yearly?: number; started?: number; canceled?: number; expired?: number };
  credit_packs: { paid_orders?: number; buyers?: number; seconds_sold?: number; revenue_minor?: number; refund_minor?: number; unconsumed_seconds?: number; by_currency?: Array<{ currency: string; revenue_minor: number; refund_minor: number }> };
  storage: Array<{ asset_role: string; object_count: number; byte_size: number }>;
  cleaned_tasks: number;
  cleanup_by_reason: Array<{ deletion_reason: string; value: number }>;
  feature_adoption: Record<string, number>;
  translations: { created?: number; completed?: number; failed?: number; source_seconds?: number; target_languages?: Array<{ target_language: string; value: number; failed: number }> };
  performance: { total_p50_ms: number | null; total_p95_ms: number | null; stages?: Record<string, { p50_ms: number | null; p95_ms: number | null }> };
  failure_codes: Array<{ error_code: string; value: number }>;
  mode_breakdown: Array<{ mode: string; created: number; completed: number; failed: number }>;
  duration_breakdown: Array<{ bucket: string; value: number }>;
  export_formats: Array<{ format: string; value: number; users: number }>;
  timezone: string;
  data_through_ms: number;
}

interface TrendMetric {
  metric_date: string;
  metric_key: string;
  value_integer: number | null;
  value_real: number | null;
  computed_at_ms: number;
}

interface PlanI18n {
  name?: string;
  description?: string;
  selling_points?: string[];
  purchase_label?: string;
}

interface PlanRow {
  code: string;
  identity_tier: "guest" | "free" | "pro";
  enabled: number;
  monthly_quota_seconds: number;
  max_file_seconds: number;
  max_file_bytes: number;
  max_batch_items: number;
  max_concurrency: number;
  queue_priority: number;
  media_retention_days: number;
  result_retention_days: number | null;
  config: { i18n?: Record<string, PlanI18n>; [key: string]: unknown };
}

interface PriceRow {
  id?: string;
  plan_code: string;
  billing_period: "month" | "year";
  currency: string;
  amount_minor: number;
  provider: string;
  provider_price_id: string;
  enabled: number;
}

interface FeatureRow {
  plan_code: string;
  feature_code: string;
  enabled: number;
  limit_value: number | null;
  config_json?: string;
}

interface CreditPackRow {
  code: string;
  seconds: number;
  currency: string;
  amount_minor: number;
  validity_days: number;
  provider: string;
  provider_price_id: string;
  enabled: number;
  config: { i18n?: Record<string, PlanI18n>; [key: string]: unknown };
  isNew?: boolean;
}

interface Catalogue {
  plans: PlanRow[];
  prices: PriceRow[];
  features: FeatureRow[];
  creditPacks: CreditPackRow[];
}

interface OperationsSettings {
  reporting_timezone: string;
  metrics_started_at_ms: number | null;
  config: Record<string, unknown>;
  updated_at_ms: number;
}

interface AuditRow {
  id: string;
  actor_principal_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  before_json: string | null;
  after_json: string | null;
  created_at_ms: number;
}

const EMPTY_OVERVIEW: Overview = {
  registered_dau: 0,
  active_guest_sessions: 0,
  new_users: 0,
  total_users: 0,
  transcription_users: {},
  confirmed_audio_seconds: 0,
  jobs: {},
  subscriptions: {},
  credit_packs: {},
  storage: [],
  cleaned_tasks: 0,
  cleanup_by_reason: [],
  feature_adoption: {},
  translations: {},
  performance: { total_p50_ms: null, total_p95_ms: null },
  failure_codes: [],
  mode_breakdown: [],
  duration_breakdown: [],
  export_formats: [],
  timezone: "Asia/Shanghai",
  data_through_ms: 0,
};

const FEATURE_CODES = [
  "speaker_diarization", "audio_enhancement", "translation", "batch_upload",
  "cloud_import", "advanced_export", "sharing", "ai_tools", "teams",
] as const;

export function AdminPage() {
  const { i18n } = useTranslation();
  const t = useMemo(() => i18n.getFixedT("zh-CN"), [i18n]);
  const { api } = useAuth();
  const [tab, setTab] = useState<AdminTab>("overview");
  const [days, setDays] = useState<7 | 30>(7);
  const [overview, setOverview] = useState<Overview>(EMPTY_OVERVIEW);
  const [trends, setTrends] = useState<TrendMetric[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue>({ plans: [], prices: [], features: [], creditPacks: [] });
  const [settings, setSettings] = useState<OperationsSettings | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [busy, setBusy] = useState<string | null>("load");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setBusy("load");
    void Promise.all([
      api.adminOverview(), api.adminPlans(), api.adminSettings(), api.adminAudit(),
    ]).then(([metrics, products, operations, history]) => {
      if (!active) return;
      setOverview({ ...EMPTY_OVERVIEW, ...metrics } as Overview);
      setCatalogue(normalizeCatalogue(products));
      setSettings(operations as unknown as OperationsSettings);
      setAudit(((history.items as AuditRow[] | undefined) ?? []));
    }).catch((reason) => active && setError(localizedError(reason, t))).finally(() => active && setBusy(null));
    return () => { active = false; };
  }, [api, t]);

  useEffect(() => {
    let active = true;
    void api.adminTrends(days).then((result) => {
      if (active) setTrends((result.items as TrendMetric[] | undefined) ?? []);
    }).catch((reason) => active && setError(localizedError(reason, t)));
    return () => { active = false; };
  }, [api, days, t]);

  async function execute(key: string, work: () => Promise<void>, success: string) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await work();
      setNotice(success);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(null);
    }
  }

  async function reloadCatalogue() {
    setCatalogue(normalizeCatalogue(await api.adminPlans()));
  }

  async function savePlan(plan: PlanRow) {
    await execute(`plan:${plan.code}`, async () => {
      await api.updateAdminPlan(plan.code, {
        enabled: plan.enabled,
        monthly_quota_seconds: plan.monthly_quota_seconds,
        max_file_seconds: plan.max_file_seconds,
        max_file_bytes: plan.max_file_bytes,
        max_batch_items: plan.max_batch_items,
        max_concurrency: plan.max_concurrency,
        queue_priority: plan.queue_priority,
        media_retention_days: plan.media_retention_days,
        result_retention_days: plan.result_retention_days,
        config_json: plan.config,
      });
      await reloadCatalogue();
    }, t("admin.savedPlan", { code: plan.code.toUpperCase() }));
  }

  async function savePrices(planCode: string) {
    await execute(`prices:${planCode}`, async () => {
      for (const period of ["month", "year"] as const) {
        const price = priceFor(catalogue.prices, planCode, period);
        const payload = { ...price, id: undefined };
        if (price.id) await api.updateAdminPrice(price.id, payload);
        else if (price.provider_price_id.trim()) await api.createAdminPrice(payload);
      }
      await reloadCatalogue();
    }, t("admin.savedPrices"));
  }

  async function saveFeatures(planCode: string) {
    await execute(`features:${planCode}`, async () => {
      await Promise.all(FEATURE_CODES.map((featureCode) => {
        const row = featureFor(catalogue.features, planCode, featureCode);
        return api.updateAdminFeature(planCode, featureCode, {
          enabled: row.enabled,
          limit_value: row.limit_value,
          config: parseJson(row.config_json),
        });
      }));
      await reloadCatalogue();
    }, t("admin.savedFeatures"));
  }

  async function savePack(pack: CreditPackRow) {
    await execute(`pack:${pack.code}`, async () => {
      const payload = {
        code: pack.code,
        seconds: pack.seconds,
        currency: pack.currency,
        amount_minor: pack.amount_minor,
        validity_days: pack.validity_days,
        provider: pack.provider,
        provider_price_id: pack.provider_price_id,
        enabled: pack.enabled,
        config: pack.config,
      };
      if (pack.isNew) await api.createAdminCreditPack(payload);
      else await api.updateAdminCreditPack(pack.code, payload);
      await reloadCatalogue();
    }, t("admin.savedPack", { code: pack.code }));
  }

  async function saveOperations() {
    if (!settings) return;
    await execute("settings", async () => {
      const result = await api.updateAdminSettings({
        reporting_timezone: settings.reporting_timezone,
        config: settings.config,
      });
      setSettings(result as unknown as OperationsSettings);
      setAudit(((await api.adminAudit()).items as AuditRow[] | undefined) ?? []);
    }, t("admin.savedOperations"));
  }

  async function rebuild(date: string) {
    await execute("rebuild", async () => {
      await api.rebuildAdminMetrics(date);
      const [metrics, trendResult, history, operations] = await Promise.all([
        api.adminOverview(), api.adminTrends(days), api.adminAudit(), api.adminSettings(),
      ]);
      setOverview({ ...EMPTY_OVERVIEW, ...metrics } as Overview);
      setTrends((trendResult.items as TrendMetric[] | undefined) ?? []);
      setAudit((history.items as AuditRow[] | undefined) ?? []);
      setSettings(operations as unknown as OperationsSettings);
    }, t("admin.rebuilt", { date }));
  }

  function patchPlan(code: string, patch: Partial<PlanRow>) {
    setCatalogue((current) => ({
      ...current,
      plans: current.plans.map((plan) => plan.code === code ? { ...plan, ...patch } : plan),
    }));
  }

  function patchPrice(planCode: string, period: "month" | "year", patch: Partial<PriceRow>) {
    setCatalogue((current) => {
      const existing = current.prices.find((item) => item.plan_code === planCode && item.billing_period === period);
      const next = existing
        ? current.prices.map((item) => item === existing ? { ...item, ...patch } : item)
        : [...current.prices, { ...priceFor([], planCode, period), ...patch }];
      return { ...current, prices: next };
    });
  }

  function patchFeature(planCode: string, featureCode: string, patch: Partial<FeatureRow>) {
    setCatalogue((current) => {
      const existing = current.features.find((item) => item.plan_code === planCode && item.feature_code === featureCode);
      const next = existing
        ? current.features.map((item) => item === existing ? { ...item, ...patch } : item)
        : [...current.features, { ...featureFor([], planCode, featureCode), ...patch }];
      return { ...current, features: next };
    });
  }

  function patchPack(code: string, patch: Partial<CreditPackRow>) {
    setCatalogue((current) => ({
      ...current,
      creditPacks: current.creditPacks.map((pack) => pack.code === code ? { ...pack, ...patch } : pack),
    }));
  }

  function addPack() {
    const code = `pack-${Date.now().toString(36)}`;
    setCatalogue((current) => ({
      ...current,
      creditPacks: [...current.creditPacks, {
        code, seconds: 36_000, currency: "USD", amount_minor: 1_500,
        validity_days: 180, provider: "generic", provider_price_id: "", enabled: 0,
        config: { i18n: { en: { name: "10 hour pack" }, "zh-CN": { name: "10 小时分钟包" } } },
        isNew: true,
      }],
    }));
  }

  const tabs: Array<[AdminTab, string]> = [
    ["overview", t("admin.tabOverview")], ["plans", t("admin.tabPlans")],
    ["packs", t("admin.tabPacks")], ["operations", t("admin.tabOperations")],
  ];

  return <div className="page admin-page">
    <header className="admin-header">
      <div><p className="eyebrow">{t("admin.eyebrow")}</p><h1>{t("admin.title")}</h1><p>{t("admin.privacyHint")}</p></div>
      <div className="admin-asof"><span>{overview.timezone}</span><strong>{overview.data_through_ms ? formatDateTime(overview.data_through_ms) : "—"}</strong></div>
    </header>
    <nav className="admin-tabs" aria-label={t("admin.sections")}>{tabs.map(([key, label]) => <button key={key} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>{label}</button>)}</nav>
    {error && <p className="form-error" role="alert">{error}</p>}
    {notice && <p className="admin-notice" role="status"><Icon name="check" />{notice}</p>}
    {busy === "load" && <div className="panel admin-loading">{t("common.loading")}</div>}
    {busy !== "load" && tab === "overview" && <OverviewPanel overview={overview} trends={trends} days={days} onDays={setDays} />}
    {busy !== "load" && tab === "plans" && <section className="admin-product-stack">{catalogue.plans.map((plan) => <PlanEditor key={plan.code} plan={plan} prices={catalogue.prices} features={catalogue.features} busy={busy} onPatch={patchPlan} onPatchPrice={patchPrice} onPatchFeature={patchFeature} onSavePlan={savePlan} onSavePrices={savePrices} onSaveFeatures={saveFeatures} />)}</section>}
    {busy !== "load" && tab === "packs" && <PacksPanel packs={catalogue.creditPacks} busy={busy} onAdd={addPack} onPatch={patchPack} onSave={savePack} />}
    {busy !== "load" && tab === "operations" && <OperationsPanel settings={settings} audit={audit} busy={busy} onSettings={setSettings} onSave={saveOperations} onRebuild={rebuild} />}
  </div>;
}

function OverviewPanel({ overview, trends, days, onDays }: { overview: Overview; trends: TrendMetric[]; days: 7 | 30; onDays: (days: 7 | 30) => void }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("zh-CN");
  const metrics = [
    ["registered_dau", t("admin.todayDau"), overview.registered_dau, t("admin.guestSessions", { count: overview.active_guest_sessions })],
    ["new_users", t("admin.newUsers"), overview.new_users, t("admin.totalUsers", { count: overview.total_users })],
    ["jobs_completed", t("admin.completed"), overview.jobs.completed ?? 0, t("admin.jobFailure", { failed: overview.jobs.failed ?? 0, rejected: overview.jobs.rejected ?? 0, created: overview.jobs.created ?? 0 })],
    ["confirmed_audio_seconds", t("admin.minutes"), Math.ceil(overview.confirmed_audio_seconds / 60), t("admin.confirmedUsage")],
  ] as const;
  const adoption = ["diarization_tasks", "audio_enhancement_tasks", "batch_tasks", "translation_created", "export_completed", "batch_export_completed", "export_emailed"];
  const stageOrder = ["upload", "queue", "preprocessing", "asr"];
  const packCurrencies = overview.credit_packs.by_currency ?? [];
  return <>
    <section className="metric-grid">{metrics.map(([key, label, value, detail]) => <article className="panel metric-card" key={key}><span>{label}</span><strong>{number(value)}</strong><small>{detail}</small><MiniBars values={trendValues(trends, key)} /></article>)}</section>
    <div className="admin-grid admin-overview-grid">
      <section className="panel trend-card"><header><div><p className="eyebrow">{t("admin.growth")}</p><h2>{t("admin.dauAndUsers")}</h2></div><div className="date-switch"><button className={days === 7 ? "is-active" : ""} onClick={() => onDays(7)}>{t("admin.days7")}</button><button className={days === 30 ? "is-active" : ""} onClick={() => onDays(30)}>{t("admin.days30")}</button></div></header><TrendChart rows={trends} first="registered_dau" second="new_users" /><footer><span><i />{t("admin.todayDau")}</span><span><i />{t("admin.newUsers")}</span></footer></section>
      <section className="panel admin-health"><header><p className="eyebrow">{t("admin.performance")}</p><h2>{t("admin.processingHealth")}</h2></header><Stat label={t("admin.completionRate")} value={percentage(overview.jobs.completion_rate)} /><Stat label={t("admin.totalP50")} value={duration(overview.performance.total_p50_ms)} /><Stat label={t("admin.totalP95")} value={duration(overview.performance.total_p95_ms)} /><Stat label={t("admin.transcriptionUsers")} value={t("admin.userSplit", { registered: overview.transcription_users.registered ?? 0, guests: overview.transcription_users.guests ?? 0 })} /></section>
    </div>
    <section className="panel admin-trend-matrix"><header><div><p className="eyebrow">{t("admin.trendCoverage")}</p><h2>{t("admin.operatingTrends")}</h2></div><p>{t("admin.trendHint", { days })}</p></header><div>{[
      ["jobs_created", t("admin.jobsCreated")],
      ["confirmed_audio_seconds", t("admin.audioUsage")],
      ["performance_total_p95_ms", t("admin.totalP95")],
      ["subscriptions_started", t("admin.subscriptionStarted")],
      ["credit_pack_orders", t("admin.packOrders")],
    ].map(([key, label]) => <TrendRow key={key} label={label} values={trendValues(trends, key)} />)}</div></section>
    <section className="admin-detail-grid">
      <article className="panel admin-fact admin-stage-fact"><header><Icon name="clock" /><h2>{t("admin.stagePerformance")}</h2></header><div className="admin-stage-head"><span>{t("admin.stage")}</span><span>P50</span><span>P95</span></div>{stageOrder.map((stage) => { const values = overview.performance.stages?.[stage]; return <div className="admin-stage-row" key={stage}><span>{t(`admin.stageName.${stage}`)}</span><strong>{duration(values?.p50_ms ?? null)}</strong><strong>{duration(values?.p95_ms ?? null)}</strong></div>; })}</article>
      <article className="panel admin-fact"><header><Icon name="more" /><h2>{t("admin.failureAnalysis")}</h2></header>{overview.failure_codes.length ? overview.failure_codes.slice(0, 8).map((row) => <Stat key={row.error_code} label={t(`admin.failure.${row.error_code}`, { defaultValue: row.error_code })} value={number(row.value)} />) : <p className="admin-empty">{t("admin.noFailures")}</p>}</article>
      <article className="panel admin-fact"><header><Icon name="wave" /><h2>{t("admin.translationAndExport")}</h2></header><Stat label={t("admin.translationCompleted")} value={number(overview.translations.completed ?? 0)} /><Stat label={t("admin.translationFailed")} value={number(overview.translations.failed ?? 0)} /><Stat label={t("admin.translationSourceMinutes")} value={number(Math.ceil((overview.translations.source_seconds ?? 0) / 60))} />{(overview.translations.target_languages ?? []).slice(0, 3).map((row) => <Stat key={row.target_language} label={row.target_language} value={number(row.value)} />)}</article>
    </section>
    <section className="admin-bottom admin-facts">
      <article className="panel admin-fact"><header><Icon name="wave" /><h2>{t("admin.adoption")}</h2></header>{adoption.map((key) => <Stat key={key} label={t(`admin.event.${key}`)} value={number(overview.feature_adoption[key] ?? 0)} />)}</article>
      <article className="panel admin-fact"><header><Icon name="chart" /><h2>{t("admin.revenue")}</h2></header><Stat label={t("admin.monthlyPro")} value={number(overview.subscriptions.monthly ?? 0)} /><Stat label={t("admin.yearlyPro")} value={number(overview.subscriptions.yearly ?? 0)} /><Stat label={t("admin.subscriptionChanges")} value={t("admin.subscriptionChangeSplit", { started: overview.subscriptions.started ?? 0, canceled: overview.subscriptions.canceled ?? 0, expired: overview.subscriptions.expired ?? 0 })} /><Stat label={t("admin.packOrders")} value={t("admin.packOrderBuyers", { orders: overview.credit_packs.paid_orders ?? 0, buyers: overview.credit_packs.buyers ?? 0 })} />{packCurrencies.length ? packCurrencies.map((row) => <Stat key={row.currency} label={t("admin.revenueByCurrency", { currency: row.currency })} value={`${money(row.revenue_minor, row.currency)} / -${money(row.refund_minor, row.currency)}`} />) : <Stat label={t("admin.packRevenue")} value={money(overview.credit_packs.revenue_minor ?? 0, "USD")} />}<Stat label={t("admin.packBalance")} value={t("common.minutes", { count: Math.floor((overview.credit_packs.unconsumed_seconds ?? 0) / 60) })} /></article>
      <article className="panel admin-fact"><header><Icon name="clock" /><h2>{t("admin.storage")}</h2></header>{overview.storage.length ? overview.storage.map((row) => <Stat key={row.asset_role} label={t(`admin.asset.${row.asset_role}`, { defaultValue: row.asset_role })} value={`${bytes(row.byte_size)} · ${number(row.object_count)}`} />) : <p className="admin-empty">{t("admin.noStorage")}</p>}<Stat label={t("admin.cleanedTasks")} value={number(overview.cleaned_tasks)} /></article>
    </section>
    <section className="admin-detail-grid admin-breakdowns">
      <article className="panel admin-fact"><header><h2>{t("admin.modeBreakdown")}</h2></header>{overview.mode_breakdown.map((row) => <Stat key={row.mode} label={t(`settings.${row.mode}`)} value={t("admin.outcomeSplit", { created: row.created, completed: row.completed, failed: row.failed })} />)}</article>
      <article className="panel admin-fact"><header><h2>{t("admin.durationBreakdown")}</h2></header>{overview.duration_breakdown.map((row) => <Stat key={row.bucket} label={t(`admin.durationBucket.${row.bucket}`)} value={number(row.value)} />)}</article>
      <article className="panel admin-fact"><header><h2>{t("admin.exportBreakdown")}</h2></header>{overview.export_formats.map((row) => <Stat key={row.format} label={row.format.toUpperCase()} value={t("admin.exportSplit", { count: row.value, users: row.users })} />)}</article>
    </section>
  </>;
}

function PlanEditor({ plan, prices, features, busy, onPatch, onPatchPrice, onPatchFeature, onSavePlan, onSavePrices, onSaveFeatures }: {
  plan: PlanRow;
  prices: PriceRow[];
  features: FeatureRow[];
  busy: string | null;
  onPatch: (code: string, patch: Partial<PlanRow>) => void;
  onPatchPrice: (code: string, period: "month" | "year", patch: Partial<PriceRow>) => void;
  onPatchFeature: (code: string, feature: string, patch: Partial<FeatureRow>) => void;
  onSavePlan: (plan: PlanRow) => Promise<void>;
  onSavePrices: (code: string) => Promise<void>;
  onSaveFeatures: (code: string) => Promise<void>;
}) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("zh-CN");
  const locale = plan.config.i18n?.["zh-CN"] ?? {};
  function patchLocale(patch: Partial<PlanI18n>) {
    onPatch(plan.code, { config: { ...plan.config, i18n: { ...plan.config.i18n, "zh-CN": { ...locale, ...patch } } } });
  }
  const integerFields: Array<[keyof PlanRow, string, number]> = [
    ["monthly_quota_seconds", t("admin.planQuota"), 60], ["max_file_seconds", t("admin.maxFileDuration"), 60],
    ["max_file_bytes", t("admin.maxFileSize"), 1024 ** 2], ["max_batch_items", t("admin.maxBatch"), 1],
    ["max_concurrency", t("admin.maxConcurrency"), 1], ["queue_priority", t("admin.queuePriority"), 1],
    ["media_retention_days", t("admin.mediaRetention"), 1],
  ];
  return <article className="panel admin-plan-card">
    <header><div><p className="eyebrow">{plan.identity_tier}</p><h2>{locale.name || plan.code.toUpperCase()}</h2><span>{t("admin.planCode", { code: plan.code })}</span></div><label className="admin-toggle"><input type="checkbox" checked={Boolean(plan.enabled)} onChange={(event) => onPatch(plan.code, { enabled: event.target.checked ? 1 : 0 })} /><i /><span>{plan.enabled ? t("admin.enabled") : t("admin.disabled")}</span></label></header>
    <div className="admin-form-grid">
      <Field label={t("admin.localizedName")}><input value={locale.name ?? ""} onChange={(event) => patchLocale({ name: event.target.value })} /></Field>
      <Field label={t("admin.purchaseLabel")}><input value={locale.purchase_label ?? ""} onChange={(event) => patchLocale({ purchase_label: event.target.value })} /></Field>
      <Field wide label={t("admin.localizedDescription")}><textarea rows={2} value={locale.description ?? ""} onChange={(event) => patchLocale({ description: event.target.value })} /></Field>
      <Field wide label={t("admin.sellingPoints")}><textarea rows={3} value={(locale.selling_points ?? []).join("\n")} onChange={(event) => patchLocale({ selling_points: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean) })} /></Field>
      {integerFields.map(([key, label, divisor]) => <Field key={key} label={label}><input type="number" min="0" value={Math.round(Number(plan[key]) / divisor)} onChange={(event) => onPatch(plan.code, { [key]: Math.max(0, Math.round(Number(event.target.value) * divisor)) })} /></Field>)}
      <Field label={t("admin.resultRetention")}><input type="number" min="0" placeholder={t("admin.forever")} value={plan.result_retention_days ?? ""} onChange={(event) => onPatch(plan.code, { result_retention_days: event.target.value === "" ? null : Math.max(0, Number(event.target.value)) })} /></Field>
    </div>
    <button className="button button--primary" disabled={busy !== null} onClick={() => void onSavePlan(plan)}><Icon name="check" />{busy === `plan:${plan.code}` ? t("common.loading") : t("admin.savePlan")}</button>
    {plan.code === "pro" && <section className="admin-subsection"><header><h3>{t("admin.priceConfiguration")}</h3><p>{t("admin.priceHint")}</p></header><div className="admin-price-grid">{(["month", "year"] as const).map((period) => <PriceEditor key={period} price={priceFor(prices, plan.code, period)} onPatch={(patch) => onPatchPrice(plan.code, period, patch)} />)}</div><button className="button button--ghost" disabled={busy !== null} onClick={() => void onSavePrices(plan.code)}>{busy === `prices:${plan.code}` ? t("common.loading") : t("admin.savePrices")}</button></section>}
    <section className="admin-subsection"><header><h3>{t("admin.featureEntitlements")}</h3><p>{t("admin.featureHint")}</p></header><div className="admin-feature-grid">{FEATURE_CODES.map((featureCode) => { const row = featureFor(features, plan.code, featureCode); return <label key={featureCode} className="admin-feature"><input type="checkbox" checked={Boolean(row.enabled)} onChange={(event) => onPatchFeature(plan.code, featureCode, { enabled: event.target.checked ? 1 : 0 })} /><span><strong>{t(`admin.feature.${featureCode}`)}</strong><code>{featureCode}</code></span><input aria-label={t("admin.featureLimit", { feature: featureCode })} type="number" min="0" placeholder="∞" value={row.limit_value ?? ""} onChange={(event) => onPatchFeature(plan.code, featureCode, { limit_value: event.target.value === "" ? null : Math.max(0, Number(event.target.value)) })} /></label>; })}</div><button className="button button--ghost" disabled={busy !== null} onClick={() => void onSaveFeatures(plan.code)}>{busy === `features:${plan.code}` ? t("common.loading") : t("admin.saveFeatures")}</button></section>
  </article>;
}

function PriceEditor({ price, onPatch }: { price: PriceRow; onPatch: (patch: Partial<PriceRow>) => void }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("zh-CN");
  return <article className="admin-price-card"><header><strong>{price.billing_period === "month" ? t("admin.monthly") : t("admin.yearly")}</strong><label><input type="checkbox" checked={Boolean(price.enabled)} onChange={(event) => onPatch({ enabled: event.target.checked ? 1 : 0 })} />{t("admin.enabled")}</label></header><div><Field label={t("admin.currency")}><select value={price.currency} onChange={(event) => onPatch({ currency: event.target.value })}><option>USD</option><option>CNY</option><option>EUR</option></select></Field><Field label={t("admin.amount")}><input type="number" min="0" step="0.01" value={(price.amount_minor / 100).toFixed(2)} onChange={(event) => onPatch({ amount_minor: Math.max(0, Math.round(Number(event.target.value) * 100)) })} /></Field><Field wide label={t("admin.providerPriceId")}><input value={price.provider_price_id} placeholder="price_…" onChange={(event) => onPatch({ provider_price_id: event.target.value })} /></Field></div></article>;
}

function PacksPanel({ packs, busy, onAdd, onPatch, onSave }: { packs: CreditPackRow[]; busy: string | null; onAdd: () => void; onPatch: (code: string, patch: Partial<CreditPackRow>) => void; onSave: (pack: CreditPackRow) => Promise<void> }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("zh-CN");
  return <section className="admin-pack-section"><header className="admin-section-heading"><div><p className="eyebrow">{t("admin.packEyebrow")}</p><h2>{t("admin.packTitle")}</h2><p>{t("admin.packHint")}</p></div><button className="button button--primary" onClick={onAdd}><Icon name="plus" />{t("admin.addPack")}</button></header>{packs.length === 0 && <div className="panel admin-empty-state">{t("admin.noPacks")}</div>}<div className="admin-pack-grid">{packs.map((pack) => { const locale = pack.config.i18n?.["zh-CN"] ?? {}; const patchLocale = (patch: Partial<PlanI18n>) => onPatch(pack.code, { config: { ...pack.config, i18n: { ...pack.config.i18n, "zh-CN": { ...locale, ...patch } } } }); return <article className="panel admin-pack-card" key={pack.code}><header><div><span>{pack.isNew ? t("admin.newPack") : pack.code}</span><h3>{locale.name || pack.code}</h3></div><label className="admin-toggle"><input type="checkbox" checked={Boolean(pack.enabled)} onChange={(event) => onPatch(pack.code, { enabled: event.target.checked ? 1 : 0 })} /><i /></label></header><div className="admin-form-grid"><Field label={t("admin.packCode")}><input value={pack.code} disabled={!pack.isNew} onChange={(event) => onPatch(pack.code, { code: event.target.value })} /></Field><Field label={t("admin.localizedName")}><input value={locale.name ?? ""} onChange={(event) => patchLocale({ name: event.target.value })} /></Field><Field label={t("admin.packMinutes")}><input type="number" min="1" value={Math.round(pack.seconds / 60)} onChange={(event) => onPatch(pack.code, { seconds: Math.max(60, Math.round(Number(event.target.value) * 60)) })} /></Field><Field label={t("admin.validityDays")}><input type="number" min="1" value={pack.validity_days} onChange={(event) => onPatch(pack.code, { validity_days: Math.max(1, Number(event.target.value)) })} /></Field><Field label={t("admin.currency")}><select value={pack.currency} onChange={(event) => onPatch(pack.code, { currency: event.target.value })}><option>USD</option><option>CNY</option><option>EUR</option></select></Field><Field label={t("admin.amount")}><input type="number" min="0.01" step="0.01" value={(pack.amount_minor / 100).toFixed(2)} onChange={(event) => onPatch(pack.code, { amount_minor: Math.max(1, Math.round(Number(event.target.value) * 100)) })} /></Field><Field wide label={t("admin.providerPriceId")}><input value={pack.provider_price_id} placeholder="price_…" onChange={(event) => onPatch(pack.code, { provider_price_id: event.target.value })} /></Field><Field wide label={t("admin.packDisclosure")}><textarea rows={2} value={locale.description ?? ""} onChange={(event) => patchLocale({ description: event.target.value })} /></Field></div><button className="button button--primary" disabled={busy !== null || !pack.code.trim() || !pack.provider_price_id.trim()} onClick={() => void onSave(pack)}>{busy === `pack:${pack.code}` ? t("common.loading") : t("admin.savePack")}</button></article>; })}</div></section>;
}

function OperationsPanel({ settings, audit, busy, onSettings, onSave, onRebuild }: { settings: OperationsSettings | null; audit: AuditRow[]; busy: string | null; onSettings: (settings: OperationsSettings) => void; onSave: () => Promise<void>; onRebuild: (date: string) => Promise<void> }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("zh-CN");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  if (!settings) return <div className="panel admin-loading">{t("common.loading")}</div>;
  return <div className="admin-operations-grid"><section className="panel admin-settings-card"><header><p className="eyebrow">{t("admin.metricSettings")}</p><h2>{t("admin.timezoneAndRebuild")}</h2></header><Field label={t("admin.timezone")}><input value={settings.reporting_timezone} disabled={settings.metrics_started_at_ms !== null} onChange={(event) => onSettings({ ...settings, reporting_timezone: event.target.value })} /></Field>{settings.metrics_started_at_ms !== null && <p className="admin-lock-note"><Icon name="check" />{t("admin.timezoneLocked")}</p>}<button className="button button--primary" disabled={busy !== null} onClick={() => void onSave()}>{busy === "settings" ? t("common.loading") : t("admin.saveOperations")}</button><hr /><Field label={t("admin.rebuildDate")}><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field><p>{t("admin.rebuildHint")}</p><button className="button button--ghost" disabled={busy !== null || !date} onClick={() => void onRebuild(date)}><Icon name="redo" />{busy === "rebuild" ? t("common.loading") : t("admin.rebuild")}</button></section><section className="panel admin-audit"><header><p className="eyebrow">{t("admin.auditEyebrow")}</p><h2>{t("admin.auditTitle")}</h2><p>{t("admin.auditHint")}</p></header><div className="admin-audit-list">{audit.length === 0 && <p className="admin-empty">{t("admin.noAudit")}</p>}{audit.map((row) => <article key={row.id}><span className="admin-audit-mark">{auditSymbol(row.action)}</span><p><strong>{t(`admin.action.${row.action}`, { defaultValue: row.action })}</strong><small>{row.resource_type} · {row.resource_id}</small></p><time>{formatDateTime(row.created_at_ms)}</time></article>)}</div></section></div>;
}

function Field({ label, wide = false, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={`admin-field${wide ? " is-wide" : ""}`}><span>{label}</span>{children}</label>;
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="admin-stat"><span>{label}</span><strong>{value}</strong></div>;
}

function MiniBars({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  return <div className="sparkline" aria-hidden="true">{(values.length ? values : [0, 0, 0, 0, 0, 0, 0]).map((value, index) => <i key={index} style={{ height: `${Math.max(3, value / max * 40)}px` }} />)}</div>;
}

function TrendRow({ label, values }: { label: string; values: number[] }) {
  return <div className="admin-trend-row"><span>{label}</span><MiniBars values={values} /><strong>{number(values.at(-1) ?? 0)}</strong></div>;
}

function TrendChart({ rows, first, second }: { rows: TrendMetric[]; first: string; second: string }) {
  const dates = [...new Set(rows.map((row) => row.metric_date))].sort();
  const firstValues = dates.map((date) => metricValue(rows, date, first));
  const secondValues = dates.map((date) => metricValue(rows, date, second));
  const maximum = Math.max(...firstValues, ...secondValues, 1);
  const points = (values: number[]) => values.map((value, index) => `${values.length === 1 ? 350 : index / Math.max(1, values.length - 1) * 700},${205 - value / maximum * 180}`).join(" ");
  return <div className="chart-area"><svg viewBox="0 0 700 220" preserveAspectRatio="none" role="img" aria-label={`${first} / ${second}`}><path className="grid-line" d="M0 25h700M0 85h700M0 145h700M0 205h700" />{dates.length > 0 && <><polyline className="line-1" points={points(firstValues)} /><polyline className="line-2" points={points(secondValues)} /></>}</svg><div className="admin-chart-dates"><span>{dates[0] ?? "—"}</span><span>{dates.at(-1) ?? "—"}</span></div></div>;
}

function normalizeCatalogue(raw: Record<string, unknown>): Catalogue {
  return {
    plans: ((raw.plans as Array<Record<string, unknown>> | undefined) ?? []).map((row) => ({ ...row, config: parseJson(row.config_json) }) as unknown as PlanRow),
    prices: ((raw.prices as PriceRow[] | undefined) ?? []),
    features: ((raw.features as FeatureRow[] | undefined) ?? []),
    creditPacks: ((raw.credit_packs as Array<Record<string, unknown>> | undefined) ?? []).map((row) => ({ ...row, config: parseJson(row.config_json) }) as unknown as CreditPackRow),
  };
}

function priceFor(prices: PriceRow[], planCode: string, period: "month" | "year"): PriceRow {
  return prices.find((item) => item.plan_code === planCode && item.billing_period === period) ?? {
    plan_code: planCode, billing_period: period, currency: "USD", amount_minor: 0,
    provider: "generic", provider_price_id: "", enabled: 0,
  };
}

function featureFor(features: FeatureRow[], planCode: string, featureCode: string): FeatureRow {
  return features.find((item) => item.plan_code === planCode && item.feature_code === featureCode) ?? {
    plan_code: planCode, feature_code: featureCode, enabled: 0, limit_value: null, config_json: "{}",
  };
}

function parseJson(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string") return {};
  try { const parsed = JSON.parse(value) as unknown; return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {}; }
  catch { return {}; }
}

function metricValue(rows: TrendMetric[], date: string, key: string): number {
  const row = rows.find((item) => item.metric_date === date && item.metric_key === key);
  return Number(row?.value_integer ?? row?.value_real ?? 0);
}

function trendValues(rows: TrendMetric[], key: string): number[] {
  return [...new Set(rows.map((row) => row.metric_date))].sort().map((date) => metricValue(rows, date, key));
}

function number(value: number): string { return new Intl.NumberFormat("zh-CN").format(value); }
function money(minor: number, currency: string): string { return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(minor / 100); }
function bytes(value: number): string { return new Intl.NumberFormat("zh-CN", { style: "unit", unit: value >= 1024 ** 3 ? "gigabyte" : "megabyte", maximumFractionDigits: 1 }).format(value / (value >= 1024 ** 3 ? 1024 ** 3 : 1024 ** 2)); }
function duration(value: number | null): string { if (value === null) return "—"; if (value < 60_000) return `${Math.round(value / 1000)} 秒`; return `${(value / 60_000).toFixed(1)} 分`; }
function percentage(value: number | null | undefined): string { return value == null ? "—" : new Intl.NumberFormat("zh-CN", { style: "percent", maximumFractionDigits: 1 }).format(value); }
function formatDateTime(value: number): string { return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "medium", timeZone: "Asia/Shanghai" }).format(value); }
function auditSymbol(action: string): string { return action.startsWith("plan") ? "P" : action.startsWith("price") ? "¥" : action.startsWith("feature") ? "F" : action.startsWith("credit") ? "+" : action.startsWith("metrics") ? "∑" : "⚙"; }
